// src/components/common/ValidatedEditableText.js
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import EditableText from '../../pages/shop/components/EditableComponents/EditableText';
import ValidationDisplay from './ValidationDisplay';
import { validateField, truncateToMaxLength } from '../../utils/inputValidation';
import { CURRENCIES } from '../../utils/currency';

const Container = styled.div`
  width: 100%;
  position: relative;
`;

const FieldContainer = styled.div`
  position: relative;
  width: 100%;
`;

const InfoIconButton = styled.button`
  position: absolute;
  right: ${props => props.hasClearButton ? '35px' : '8px'};
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: ${props => props.theme?.colors?.text || '#333'}80;
  cursor: help;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${props => props.theme?.colors?.accent || '#800000'};
    background: ${props => props.theme?.colors?.accent || '#800000'}10;
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const CurrencySelect = styled.select`
  position: absolute;
  right: ${props => props.hasClearButton ? '32px' : '4px'};
  top: 50%;
  transform: translateY(-50%);
  background: ${props => props.theme?.colors?.surface || props.theme?.colors?.background || '#1a1a1a'};
  border: 1px solid ${props => props.theme?.colors?.accent || '#800000'}60;
  color: ${props => props.theme?.colors?.text || '#fff'};
  border-radius: 6px;
  padding: 2px 4px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  outline: none;
  max-width: 62px;

  &:hover {
    border-color: ${props => props.theme?.colors?.accent || '#800000'};
  }

  option {
    background: ${props => props.theme?.colors?.surface || props.theme?.colors?.background || '#1a1a1a'};
    color: ${props => props.theme?.colors?.text || '#fff'};
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: ${props => props.theme?.colors?.text || '#333'}60;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.2s ease;
  opacity: ${props => props.show ? 1 : 0};
  pointer-events: ${props => props.show ? 'auto' : 'none'};
  
  &:hover {
    color: ${props => props.theme?.colors?.error || '#ff4444'};
    background: ${props => props.theme?.colors?.error || '#ff4444'}10;
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const InfoTooltip = styled.div`
  position: absolute;
  top: -140px;
  right: 0;
  width: 280px;
  background: ${props => props.theme?.colors?.surface || '#fff'};
  border: 1px solid ${props => props.theme?.colors?.accent || '#800000'}40;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  z-index: 1000;
  opacity: ${props => props.show ? 1 : 0};
  transform: ${props => props.show ? 'translateY(0)' : 'translateY(10px)'};
  pointer-events: ${props => props.show ? 'auto' : 'none'};
  transition: all 0.3s ease;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    right: 20px;
    width: 16px;
    height: 16px;
    background: ${props => props.theme?.colors?.surface || '#fff'};
    border-right: 1px solid ${props => props.theme?.colors?.accent || '#800000'}40;
    border-bottom: 1px solid ${props => props.theme?.colors?.accent || '#800000'}40;
    transform: rotate(45deg);
  }
`;

const TooltipTitle = styled.div`
  font-weight: 700;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  margin-bottom: 8px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TooltipContent = styled.div`
  font-size: 12px;
  line-height: 1.4;
  color: ${props => props.theme?.colors?.text || '#333'};
`;

const TooltipNote = styled.div`
  margin-top: 8px;
  padding: 8px;
  background: ${props => props.theme?.colors?.accent || '#800000'}10;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  border-left: 3px solid ${props => props.theme?.colors?.accent || '#800000'};
`;

const ValidatedEditableText = ({
  value,
  onChange,
  placeholder,
  multiline = false,
  validationRules,
  showValidation = true,
  autoTruncate = true,
  theme,
  fontSize,
  fontWeight,
  minHeight,
  style,
  showClearButton = false,
  showInfoButton = false,
  infoTooltipContent = null,
  showCurrencySelector = false,
  currencyValue = 'USD',
  onCurrencyChange,
  ...otherProps
}) => {
  const [validation, setValidation] = useState({
    isValid: true,
    error: null,
    charCount: 0,
    charLimit: validationRules?.maxLength
  });

  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (validationRules) {
      const result = validateField(value || '', validationRules);
      setValidation(result);
    }
  }, [value, validationRules]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target) &&
          containerRef.current && !containerRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTooltip]);

  const handleChange = (newValue) => {
    let processedValue = newValue;

    // Auto-truncate if enabled
    if (autoTruncate && validationRules?.maxLength) {
      processedValue = truncateToMaxLength(newValue, validationRules.maxLength);
    }

    // Validate pattern if exists (only for non-empty values)
    if (validationRules?.pattern && processedValue && processedValue.trim()) {
      if (!validationRules.pattern.test(processedValue)) {
        // For pattern validation, allow backspace/deletion
        // Only block if they're adding invalid characters
        if (processedValue.length > (value?.length || 0)) {
          return;
        }
      }
    }

    onChange(processedValue);
  };

  const handleClear = () => {
    onChange('');
  };

  const getPriceTooltipContent = () => (
    <>
      <TooltipNote>
        Note: 7% of this price is held for Stipe and Platform Fees. As a Seller, you are responsible for all applicable taxes. Price should include any taxes you need to collect.
      </TooltipNote>
    </>
  );

  return (
    <Container ref={containerRef}>
      <FieldContainer>
        <EditableText
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          multiline={multiline}
          theme={theme}
          fontSize={fontSize}
          fontWeight={fontWeight}
          minHeight={minHeight}
          maxLength={validationRules?.maxLength}
          style={{
            ...style,
            paddingRight: (showClearButton || showInfoButton || showCurrencySelector) ? '48px' : '12px'
          }}
          {...otherProps}
        />

        {showCurrencySelector && (
          <CurrencySelect
            theme={theme}
            hasClearButton={showClearButton && value}
            value={currencyValue}
            onClick={e => e.stopPropagation()}
            onChange={e => onCurrencyChange && onCurrencyChange(e.target.value)}
            title="Currency"
          >
            {CURRENCIES.map(c => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </CurrencySelect>
        )}

        {showInfoButton && (
          <>
            <InfoIconButton
              theme={theme}
              hasClearButton={showClearButton && value}
              onClick={() => setShowTooltip(!showTooltip)}
              title="Price information"
              type="button"
            >
              ℹ️
            </InfoIconButton>
            
            <InfoTooltip
              ref={tooltipRef}
              theme={theme}
              show={showTooltip}
            >
              {infoTooltipContent || getPriceTooltipContent()}
            </InfoTooltip>
          </>
        )}

        {showClearButton && (
          <ClearButton
            theme={theme}
            show={!!value}
            onClick={handleClear}
            title="Clear field"
            type="button"
          >
            ✕
          </ClearButton>
        )}
      </FieldContainer>
      
      {showValidation && validationRules && (
        <ValidationDisplay
          error={validation.error}
          charCount={validation.charCount}
          charLimit={validation.charLimit}
          theme={theme}
        />
      )}
    </Container>
  );
};

export default ValidatedEditableText;